import { useMemo } from 'react'
import { useAuth } from '../../Contexts/AuthContext'
import useAdminAccess from './useAdminAccess'

/**
 * Get organization and permission information for display purposes.
 * Shows which org/group is providing access and what role the user has.
 *
 * @param {Object} item - The item object containing an organizations array
 * @returns {Object} { orgName, groupName, orgImage, role, hasAccess }
 */
export default function useOrgPermissionInfo(item) {
  const { getCurrentUser } = useAuth()
  const { isInternal } = useAdminAccess()
  const user = getCurrentUser()

  return useMemo(() => {
    // Internal users show as "Internal"
    if (isInternal) {
      return {
        orgName: 'Feels Music',
        groupName: null,
        orgImage: null,
        role: 'Internal',
        hasAccess: true,
      }
    }

    // No item or no user
    if (!item || !user) {
      return {
        orgName: null,
        groupName: null,
        orgImage: null,
        role: null,
        hasAccess: false,
      }
    }

    // Try to find organizations - check item directly, then nested song/artist
    const organizations = item.organizations?.length > 0
      ? item.organizations
      : item.song?.organizations?.length > 0
        ? item.song.organizations
        : item.artist?.organizations?.length > 0
          ? item.artist.organizations
          : null

    // No organizations found
    if (!organizations || organizations.length === 0) {
      return {
        orgName: null,
        groupName: null,
        orgImage: null,
        role: 'Viewer',
        hasAccess: true, // Can view but not edit
      }
    }

    // Find highest permission across all org/group combinations
    let bestMatch = null
    let highestRoleRank = -1

    const roleRankings = {
      'Owner': 5,
      'Organization Owner': 5,
      'Admin': 4,
      'Organization Administrator': 4,
      'Organization Admin': 4,
      'Group Administrator': 4,
      'Team Administrator': 4,
      'Editor': 3,
      'Organization Editor': 3,
      'Viewer': 2,
      'Organization Viewer': 2,
    }

    // Map role names to display-friendly versions
    const roleDisplayNames = {
      'Group Administrator': 'Team Administrator',
      'Organization Owner': 'Owner',
      'Organization Administrator': 'Administrator',
      'Organization Admin': 'Administrator',
      'Organization Editor': 'Editor',
      'Organization Viewer': 'Viewer',
    }

    // Loop through item's organizations
    for (const itemOrg of organizations) {
      // Find this org in user's account data
      const userOrg = (user.organizations || []).find(
        org => org.f_id === itemOrg.organization_f_id
      )

      if (!userOrg) continue

      // Loop through item's groups
      for (const itemGroupId of itemOrg.organization_groups || []) {
        const userGroup = (
          userOrg.organization_user?.organization_groups || []
        ).find(g => g.f_id === itemGroupId)

        if (!userGroup) continue

        // Get highest role in this group
        const roles = userGroup.roles || []
        for (const roleObj of roles) {
          const roleName = roleObj.role
          const roleRank = roleRankings[roleName] || 0

          if (roleRank > highestRoleRank) {
            highestRoleRank = roleRank
            const displayRole = roleDisplayNames[roleName] || roleName
            bestMatch = {
              orgName: userOrg.name,
              groupName: userGroup.name !== 'Organization' ? userGroup.name : null,
              orgImage: userOrg.image_url || null,
              role: displayRole,
              hasAccess: true,
            }
          }
        }

        // If no role, check for permissions to infer role
        if (roles.length === 0) {
          const permissions = userGroup.permissions || []
          const hasWrite = permissions.some(p => !p.endsWith(':r'))

          if (hasWrite && highestRoleRank < 3) {
            bestMatch = {
              orgName: userOrg.name,
              groupName: userGroup.name !== 'Organization' ? userGroup.name : null,
              orgImage: userOrg.image_url || null,
              role: 'Editor',
              hasAccess: true,
            }
            highestRoleRank = 3
          } else if (permissions.length > 0 && highestRoleRank < 2) {
            bestMatch = {
              orgName: userOrg.name,
              groupName: userGroup.name !== 'Organization' ? userGroup.name : null,
              orgImage: userOrg.image_url || null,
              role: 'Viewer',
              hasAccess: true,
            }
            highestRoleRank = 2
          }
        }
      }
    }

    return bestMatch || {
      orgName: null,
      groupName: null,
      orgImage: null,
      role: null,
      hasAccess: false,
    }
  }, [item, user, isInternal])
}
